import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Accordion from '../../../../components/Accordion';
import FormInput from '../../../../components/Form/FormInput';
import Table, {
  ColumnDefinitionType,
} from '../../../../components/Table';
import { usePlanEmergencia } from '../../../../hooks/usePlanEmergencia';
import { useSolicitud } from '../../../../hooks/useSolicitud';
import { useSolicitudPermissions } from '../../../../hooks/useSolicitudPermissions';
import { PersonalEmergencia } from '../../../../types/PlanEmergencia';
import { logger } from '../../../../utils/logger';
import {
  PRE_APROBADA_STATUS_ID,
  APROBADA_RT_STATUS_ID,
} from '../../../../constants/constants';

type PlanEmergenciaProps = {
  isDisabled?: boolean;
  isEdit?: boolean;
};

const PlanEmergencia: React.FC<PlanEmergenciaProps> = ({
  isDisabled = false,
  isEdit = true,
}) => {
  const [cargarPlanEmergencia, setCargarPlanEmergencia] =
    useState(true);
  const [isGuardando, setIsGuardando] = useState(false);
  const [errores, setErrores] = useState<{ [key: string]: string }>(
    {}
  );

  const { solicitudSelected, buscarSolicitud } = useSolicitud();
  const {
    planEmergenciaSelected,
    isLoading,
    tieneRiesgoModeradoAlto,
    buscarPlanEmergenciaByIdSolicitud,
    obtenerPersonalEmergencia,
    verificarRiesgoModeradoAlto,
    guardarPlanEmergencia,
    actualizarCentroDerivacion,
    actualizarRolContingencia,
    actualizarCampo,
    convertirAPlanEmergenciaForm,
  } = usePlanEmergencia();

  const { canEditPlan, esAnalistaSeguridad } = useSolicitudPermissions({
    solicitudEstadoId: solicitudSelected.idTipoEstado,
  });

  // CU55: replica el criterio de Análisis de Riesgo — el Analista de Seguridad
  // (Empresa Gestora) puede editar el Plan de Emergencia en PRE_APROBADA / APROBADA_RT.
  // Para el resto de usuarios se conserva el comportamiento previo (incluye !isDisabled).
  const canEditPlanEmergencia = React.useMemo(() => {
    if (
      esAnalistaSeguridad &&
      (solicitudSelected.idTipoEstado === PRE_APROBADA_STATUS_ID ||
        solicitudSelected.idTipoEstado === APROBADA_RT_STATUS_ID)
    ) {
      return isEdit;
    }
    return isEdit && canEditPlan && !isDisabled;
  }, [
    esAnalistaSeguridad,
    solicitudSelected.idTipoEstado,
    isEdit,
    canEditPlan,
    isDisabled,
  ]);

  // Cuando el analista puede editar, la solicitud está read-only (isDisabled=true por
  // isFormDisabled), pero esa restricción no debe aplicar a los controles del Plan de Emergencia.
  const isDisabledEfectivo = isDisabled && !canEditPlanEmergencia;

  const [isEditing, setIsEditing] = useState(canEditPlanEmergencia);

  // Cargar datos cuando se hace click en el accordion
  const handleClick = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (cargarPlanEmergencia && solicitudSelected.id) {
      try {
        // Cargar plan de emergencia, verificar riesgo y obtener personal en paralelo
        await Promise.all([
          buscarPlanEmergenciaByIdSolicitud(solicitudSelected.id),
          verificarRiesgoModeradoAlto(solicitudSelected.id),
          obtenerPersonalEmergencia(solicitudSelected.id),
        ]);
        setCargarPlanEmergencia(false);
        setIsEditing(canEditPlanEmergencia);
      } catch (error) {
        logger.error('Error al cargar plan de emergencia:', logger.safeError(error));
        toast.error(
          'Error al cargar los datos del plan de emergencia'
        );
      }
    }
  };

  // Actualizar estado de edición cuando cambie el permiso
  useEffect(() => {
    setIsEditing(canEditPlanEmergencia);
  }, [canEditPlanEmergencia]);

  // Función para guardar el plan de emergencia
  const handleGuardar = async () => {
    try {
      setIsGuardando(true);
      setErrores({}); // Limpiar errores previos

      if (!solicitudSelected.id) {
        toast.error('No se ha seleccionado una solicitud');
        return;
      }

      // Objeto para acumular errores
      const nuevosErrores: { [key: string]: string } = {};

      // Validaciones básicas
      const artValue = String(
        planEmergenciaSelected.art || ''
      ).trim();
      if (!artValue) {
        nuevosErrores.art = 'El campo ART es obligatorio';
      }

      const nroContratoValue = String(
        planEmergenciaSelected.nroContrato || ''
      ).trim();
      if (!nroContratoValue) {
        nuevosErrores.nroContrato =
          'El campo Nro. Contrato es obligatorio';
      }

      // Validar Centro de Derivación 1
      const nombre1Value = String(
        planEmergenciaSelected.nombre1 || ''
      ).trim();
      if (!nombre1Value) {
        nuevosErrores.nombre1 = 'El nombre es obligatorio';
      }

      const direccion1Value = String(
        planEmergenciaSelected.direccion1 || ''
      ).trim();
      if (!direccion1Value) {
        nuevosErrores.direccion1 = 'La dirección es obligatoria';
      }

      // Validar Roles de Contingencia obligatorios
      const incendioValue = String(
        planEmergenciaSelected.incendio || ''
      ).trim();
      if (!incendioValue) {
        nuevosErrores.incendio = 'El campo Incendio es obligatorio';
      }

      const rescateHombreAguaValue = String(
        planEmergenciaSelected.rescateHombreAgua || ''
      ).trim();
      if (!rescateHombreAguaValue) {
        nuevosErrores.rescateHombreAgua =
          'El campo Rescate Hombre al Agua es obligatorio';
      }

      const fugaDerrameValue = String(
        planEmergenciaSelected.fugaDerrame || ''
      ).trim();
      if (!fugaDerrameValue) {
        nuevosErrores.fugaDerrame =
          'El campo Fuga/Derrame es obligatorio';
      }

      const accidentePersonalValue = String(
        planEmergenciaSelected.accidentePersonal || ''
      ).trim();
      if (!accidentePersonalValue) {
        nuevosErrores.accidentePersonal =
          'El campo Accidente Personal es obligatorio';
      }

      const rescateECValue = String(
        planEmergenciaSelected.rescateEC || ''
      ).trim();
      if (!rescateECValue) {
        nuevosErrores.rescateEC =
          'El campo Rescate en EC es obligatorio';
      }

      const rescateAlturaValue = String(
        planEmergenciaSelected.rescateAltura || ''
      ).trim();
      if (!rescateAlturaValue) {
        nuevosErrores.rescateAltura =
          'El campo Rescate en Altura es obligatorio';
      }

      // Validar Metodología y Equipamiento solo si tiene riesgo moderado/alto
      if (tieneRiesgoModeradoAlto) {
        const metodologiaValue = String(
          planEmergenciaSelected.metodologiaAtencion || ''
        ).trim();
        if (!metodologiaValue) {
          nuevosErrores.metodologiaAtencion =
            'El campo Metodología de Atención es obligatorio';
        }

        const equipamientoValue = String(
          planEmergenciaSelected.equipamientoEmergencia || ''
        ).trim();
        if (!equipamientoValue) {
          nuevosErrores.equipamientoEmergencia =
            'El campo Equipamiento para Emergencia es obligatorio';
        }
      }

      // Si hay errores, mostrarlos y detener
      if (Object.keys(nuevosErrores).length > 0) {
        setErrores(nuevosErrores);
        toast.error(
          'Por favor, complete todos los campos obligatorios'
        );
        return;
      }

      // Preparar datos para enviar - convertir a formato de formulario
      const planEmergenciaParaGuardar = {
        ...planEmergenciaSelected,
        idSolicitud: solicitudSelected.id,
      };

      const planEmergenciaForm = convertirAPlanEmergenciaForm(
        planEmergenciaParaGuardar
      );

      await guardarPlanEmergencia(planEmergenciaForm);
      toast.success('Plan de emergencia guardado exitosamente');

      // Refrescar la solicitud para reflejar la transición de estado
      // (APROBADA → ACTUALIZACIÓN) sin necesidad de recargar la página
      await buscarSolicitud(String(solicitudSelected.id));
    } catch (error) {
      logger.error('Error al guardar plan de emergencia:', logger.safeError(error));
      toast.error('Error al guardar el plan de emergencia');
    } finally {
      setIsGuardando(false);
    }
  };

  const handleCancelar = () => {
    toast.info('Operación cancelada');
  };

  // Definición de columnas para la tabla de Personal de Emergencia (solo lectura)
  const columnsPersonalEmergencia: ColumnDefinitionType<
    PersonalEmergencia,
    keyof PersonalEmergencia
  >[] = [];
  columnsPersonalEmergencia.push({
    key: 'nombreCompleto',
    header: 'PERSONAL',
  });
  columnsPersonalEmergencia.push({
    key: 'rol',
    header: 'ROL',
  });

  return (
    <Accordion
      id={'planEmergencia'}
      title="Plan de Emergencia"
      collapsed={true}
      handleClick={handleClick}
    >
      {isLoading ? (
        <div className="d-flex justify-content-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : (
        <div>
          {/* Título de sección En Caso de Accidentes */}
          <div className="row mt-3">
            <div className="col-12">
              <h6
                className="mb-3"
                style={{
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ccc',
                  paddingBottom: '8px',
                }}
              >
                En Caso de Accidentes
              </h6>
            </div>
          </div>

          <div className="row mt-2">
            <FormInput
              id="art"
              label="ART"
              name="art"
              value={planEmergenciaSelected.art}
              isDisabled={isDisabledEfectivo || !isEditing}
              maxLength={50}
              onChange={(e) => {
                actualizarCampo('art', e.target.value);
                if (errores.art) {
                  setErrores({ ...errores, art: '' });
                }
              }}
              classColumn="col-3"
            />
            <FormInput
              id="nroContrato"
              label="Nro. Contrato"
              name="nroContrato"
              type="text"
              value={planEmergenciaSelected.nroContrato}
              isDisabled={isDisabledEfectivo || !isEditing}
              maxLength={11}
              onChange={(e) => {
                // Solo permitir números
                const value = e.target.value.replace(/\D/g, '');
                actualizarCampo('nroContrato', value);
                if (errores.nroContrato) {
                  setErrores({ ...errores, nroContrato: '' });
                }
              }}
              classColumn="col-3"
            />
          </div>
          {(errores.art || errores.nroContrato) && (
            <div className="row">
              <div className="col-2"></div>
              <div className="col-3">
                {errores.art && (
                  <small className="text-danger">{errores.art}</small>
                )}
              </div>
              <div className="col-2"></div>
              <div className="col-3">
                {errores.nroContrato && (
                  <small className="text-danger">
                    {errores.nroContrato}
                  </small>
                )}
              </div>
            </div>
          )}

          {/* Centro de Derivación 1 */}
          <div className="row mt-2">
            <label className="col-form-label col-3 text-center">
              Centro de Derivación 1
            </label>
          </div>
          <div className="row mt-2">
            <label className="col-form-label col-2 text-end">
              Nombre
            </label>
            <div className="col-3">
              <input
                type="text"
                className="form-control"
                value={planEmergenciaSelected.nombre1 || ''}
                maxLength={255}
                disabled={isDisabledEfectivo || !isEditing}
                onChange={(e) => {
                  actualizarCentroDerivacion(
                    0,
                    'nombre',
                    e.target.value
                  );
                  if (errores.nombre1) {
                    setErrores({ ...errores, nombre1: '' });
                  }
                }}
              />
              {errores.nombre1 && (
                <small className="text-danger d-block">
                  {errores.nombre1}
                </small>
              )}
            </div>
            <label className="col-form-label col-2 text-end">
              Dirección
            </label>
            <div className="col-3">
              <input
                type="text"
                className="form-control"
                value={planEmergenciaSelected.direccion1 || ''}
                disabled={isDisabledEfectivo || !isEditing}
                maxLength={255}
                onChange={(e) => {
                  actualizarCentroDerivacion(
                    0,
                    'direccion',
                    e.target.value
                  );
                  if (errores.direccion1) {
                    setErrores({ ...errores, direccion1: '' });
                  }
                }}
              />
              {errores.direccion1 && (
                <small className="text-danger d-block">
                  {errores.direccion1}
                </small>
              )}
            </div>
          </div>

          {/* Centro de Derivación 2 */}
          <div className="row mt-2">
            <label className="col-form-label col-3 text-center">
              Centro de Derivación 2
            </label>
          </div>
          <div className="row mt-2">
            <label className="col-form-label col-2 text-end">
              Nombre
            </label>
            <div className="col-3">
              <input
                type="text"
                className="form-control"
                value={planEmergenciaSelected.nombre2 || ''}
                disabled={isDisabledEfectivo || !isEditing}
                maxLength={255}
                onChange={(e) =>
                  actualizarCentroDerivacion(
                    1,
                    'nombre',
                    e.target.value
                  )
                }
              />
            </div>
            <label className="col-form-label col-2 text-end">
              Dirección
            </label>
            <div className="col-3">
              <input
                type="text"
                className="form-control"
                value={planEmergenciaSelected.direccion2 || ''}
                disabled={isDisabledEfectivo || !isEditing}
                maxLength={255}
                onChange={(e) =>
                  actualizarCentroDerivacion(
                    1,
                    'direccion',
                    e.target.value
                  )
                }
              />
            </div>
          </div>

          {/* Centro de Derivación 3 */}
          <div className="row mt-2">
            <label className="col-form-label col-3 text-center">
              Centro de Derivación 3
            </label>
          </div>
          <div className="row mt-2">
            <label className="col-form-label col-2 text-end">
              Nombre
            </label>
            <div className="col-3">
              <input
                type="text"
                className="form-control"
                value={planEmergenciaSelected.nombre3 || ''}
                disabled={isDisabledEfectivo || !isEditing}
                maxLength={255}
                onChange={(e) =>
                  actualizarCentroDerivacion(
                    2,
                    'nombre',
                    e.target.value
                  )
                }
              />
            </div>
            <label className="col-form-label col-2 text-end">
              Dirección
            </label>
            <div className="col-3">
              <input
                type="text"
                className="form-control"
                value={planEmergenciaSelected.direccion3 || ''}
                disabled={isDisabledEfectivo || !isEditing}
                maxLength={255}
                onChange={(e) =>
                  actualizarCentroDerivacion(
                    2,
                    'direccion',
                    e.target.value
                  )
                }
              />
            </div>
          </div>

          {/* Título de sección Rol de Contingencia que Aplica */}
          <div className="row mt-4">
            <div className="col-12">
              <h6
                className="mb-3"
                style={{
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ccc',
                  paddingBottom: '8px',
                }}
              >
                Rol de Contingencia que Aplica
              </h6>
            </div>
          </div>

          <div className="row mt-2">
            {/* Columna izquierda - Primeros 3 roles */}
            <div className="col-6">
              <div className="row mt-2">
                <label className="col-form-label col-4 text-end">
                  Incendio
                </label>
                <div className="col-8">
                  <input
                    type="text"
                    className="form-control"
                    value={planEmergenciaSelected.incendio || ''}
                    disabled={isDisabledEfectivo || !isEditing}
                    maxLength={255}
                    onChange={(e) => {
                      actualizarRolContingencia(
                        'incendio',
                        e.target.value
                      );
                      if (errores.incendio) {
                        setErrores({ ...errores, incendio: '' });
                      }
                    }}
                  />
                  {errores.incendio && (
                    <small className="text-danger d-block">
                      {errores.incendio}
                    </small>
                  )}
                </div>
              </div>
              <div className="row mt-2">
                <label className="col-form-label col-4 text-end">
                  Rescate Hombre al agua
                </label>
                <div className="col-8">
                  <input
                    type="text"
                    className="form-control"
                    value={
                      planEmergenciaSelected.rescateHombreAgua || ''
                    }
                    disabled={isDisabledEfectivo || !isEditing}
                    maxLength={255}
                    onChange={(e) => {
                      actualizarRolContingencia(
                        'rescateHombreAgua',
                        e.target.value
                      );
                      if (errores.rescateHombreAgua) {
                        setErrores({
                          ...errores,
                          rescateHombreAgua: '',
                        });
                      }
                    }}
                  />
                  {errores.rescateHombreAgua && (
                    <small className="text-danger d-block">
                      {errores.rescateHombreAgua}
                    </small>
                  )}
                </div>
              </div>
              <div className="row mt-2">
                <label className="col-form-label col-4 text-end">
                  Fuga Derrame
                </label>
                <div className="col-8">
                  <input
                    type="text"
                    className="form-control"
                    value={planEmergenciaSelected.fugaDerrame || ''}
                    disabled={isDisabledEfectivo || !isEditing}
                    maxLength={255}
                    onChange={(e) => {
                      actualizarRolContingencia(
                        'fugaDerrame',
                        e.target.value
                      );
                      if (errores.fugaDerrame) {
                        setErrores({ ...errores, fugaDerrame: '' });
                      }
                    }}
                  />
                  {errores.fugaDerrame && (
                    <small className="text-danger d-block">
                      {errores.fugaDerrame}
                    </small>
                  )}
                </div>
              </div>
            </div>

            {/* Columna derecha - Últimos 3 roles */}
            <div className="col-6">
              <div className="row mt-2">
                <label className="col-form-label col-4 text-end">
                  Accidente Personal
                </label>
                <div className="col-8">
                  <input
                    type="text"
                    className="form-control"
                    value={
                      planEmergenciaSelected.accidentePersonal || ''
                    }
                    disabled={isDisabledEfectivo || !isEditing}
                    maxLength={255}
                    onChange={(e) => {
                      actualizarRolContingencia(
                        'accidentePersonal',
                        e.target.value
                      );
                      if (errores.accidentePersonal) {
                        setErrores({
                          ...errores,
                          accidentePersonal: '',
                        });
                      }
                    }}
                  />
                  {errores.accidentePersonal && (
                    <small className="text-danger d-block">
                      {errores.accidentePersonal}
                    </small>
                  )}
                </div>
              </div>
              <div className="row mt-2">
                <label className="col-form-label col-4 text-end">
                  Rescate en EC
                </label>
                <div className="col-8">
                  <input
                    type="text"
                    className="form-control"
                    value={planEmergenciaSelected.rescateEC || ''}
                    disabled={isDisabledEfectivo || !isEditing}
                    maxLength={255}
                    onChange={(e) => {
                      actualizarRolContingencia(
                        'rescateEC',
                        e.target.value
                      );
                      if (errores.rescateEC) {
                        setErrores({ ...errores, rescateEC: '' });
                      }
                    }}
                  />
                  {errores.rescateEC && (
                    <small className="text-danger d-block">
                      {errores.rescateEC}
                    </small>
                  )}
                </div>
              </div>
              <div className="row mt-2">
                <label className="col-form-label col-4 text-end">
                  Rescate en Altura
                </label>
                <div className="col-8">
                  <input
                    type="text"
                    className="form-control"
                    value={planEmergenciaSelected.rescateAltura || ''}
                    disabled={isDisabledEfectivo || !isEditing}
                    maxLength={255}
                    onChange={(e) => {
                      actualizarRolContingencia(
                        'rescateAltura',
                        e.target.value
                      );
                      if (errores.rescateAltura) {
                        setErrores({ ...errores, rescateAltura: '' });
                      }
                    }}
                  />
                  {errores.rescateAltura && (
                    <small className="text-danger d-block">
                      {errores.rescateAltura}
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Metodología de Atención y Equipamiento - Solo si tiene riesgo moderado/alto */}
          {tieneRiesgoModeradoAlto && (
            <>
              {/* Título de sección Metodología de Atención */}
              <div className="row mt-4">
                <div className="col-12">
                  <h6
                    className="mb-3"
                    style={{
                      fontWeight: 'bold',
                      borderBottom: '1px solid #ccc',
                      paddingBottom: '8px',
                    }}
                  >
                    Metodología de Atención
                  </h6>
                  <p className="text-muted mb-3">
                    Describir pasos a seguir en caso de ocurrir una
                    emergencia.
                  </p>
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-1"></div>
                <div className="col-10">
                  <textarea
                    className="form-control"
                    rows={4}
                    value={planEmergenciaSelected.metodologiaAtencion}
                    disabled={isDisabledEfectivo || !isEditing}
                    maxLength={255}
                    onChange={(e) => {
                      actualizarCampo(
                        'metodologiaAtencion',
                        e.target.value
                      );
                      if (errores.metodologiaAtencion) {
                        setErrores({
                          ...errores,
                          metodologiaAtencion: '',
                        });
                      }
                    }}
                    placeholder="Ingrese la metodología de atención..."
                  />
                  {errores.metodologiaAtencion && (
                    <small className="text-danger d-block">
                      {errores.metodologiaAtencion}
                    </small>
                  )}
                </div>
                <div className="col-1"></div>
              </div>

              {/* Título de sección Equipamiento para Emergencia */}
              <div className="row mt-4">
                <div className="col-12">
                  <h6
                    className="mb-3"
                    style={{
                      fontWeight: 'bold',
                      borderBottom: '1px solid #ccc',
                      paddingBottom: '8px',
                    }}
                  >
                    Equipamiento para Emergencia
                  </h6>
                  <p className="text-muted mb-3">
                    Describir el equipamiento necesario o sistema de
                    rescate para utilizar en caso de una emergencia
                    (Espacio confinado, trabajo en altura, incendio).
                  </p>
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-1"></div>
                <div className="col-10">
                  <textarea
                    className="form-control"
                    rows={4}
                    value={
                      planEmergenciaSelected.equipamientoEmergencia
                    }
                    disabled={isDisabledEfectivo || !isEditing}
                    maxLength={255}
                    onChange={(e) => {
                      actualizarCampo(
                        'equipamientoEmergencia',
                        e.target.value
                      );
                      if (errores.equipamientoEmergencia) {
                        setErrores({
                          ...errores,
                          equipamientoEmergencia: '',
                        });
                      }
                    }}
                    placeholder="Ingrese el equipamiento para emergencia..."
                  />
                  {errores.equipamientoEmergencia && (
                    <small className="text-danger d-block">
                      {errores.equipamientoEmergencia}
                    </small>
                  )}
                </div>
                <div className="col-1"></div>
              </div>
            </>
          )}

          {/* Título de sección Personal de Emergencia */}
          <div className="row mt-4">
            <div className="col-12">
              <h6
                className="mb-3"
                style={{
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ccc',
                  paddingBottom: '8px',
                }}
              >
                Personal de Emergencia
              </h6>
            </div>
          </div>

          <div className="p-3">
            <div className="table-responsive">
              <Table
                data={planEmergenciaSelected.personalEmergencia || []}
                columns={columnsPersonalEmergencia}
                notResults="No existe Personal de Emergencia"
              />
            </div>
          </div>

          {/* Botones de acción */}
          {isEditing && !isDisabledEfectivo && (
            <div className="text-center">
              <button
                type="button"
                className="btn btn-danger mx-2"
                onClick={handleCancelar}
                disabled={isGuardando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-info mx-2"
                onClick={handleGuardar}
                disabled={isGuardando}
              >
                {isGuardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          )}

          <div className="row mt-3"></div>
        </div>
      )}
    </Accordion>
  );
};

export default PlanEmergencia;
